import { toPng } from 'html-to-image';

const IMAGE_CACHE = new Map<string, { dataUrl: string; iconSize: [number, number] }>();
const cacheWorking = new Set();
// wait till we are doing processing the current cache item.
// otherwise we can end up with lots of network request loading images.
const waitForCache = (key: string) => {
  return new Promise<boolean>((res) => {
    if (!cacheWorking.has(key)) {
      res(true);
      return;
    }
    const interval = setInterval(() => {
      if (!cacheWorking.has(key)) {
        res(true);
        clearInterval(interval);
      }
    }, 5);
  });
};

export function getFromCache(htmlString: string) {
  return IMAGE_CACHE.get(getHtmlImageCacheKey(htmlString));
}

export function getHtmlImageCacheKey(htmlString: string) {
  //return imurmurhash(htmlString).result();
  return htmlString;
}

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // Radius of the earth in kilometers
  const dLat = degToRad(lat2 - lat1);
  const dLon = degToRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c; // Distance in kilometers
  return distance;
};

function degToRad(deg: number) {
  return deg * (Math.PI / 180);
}

export function getScaledDuration(
  prevLat: number,
  prevLon: number,
  newLat: number,
  newLon: number,
  prevZoom: number,
  newZoom: number
) {
  const distance = calculateDistance(prevLat, prevLon, newLat, newLon);
  const maxDistance = 1000;
  const maxDuration = 3000; // Maximum duration in milliseconds
  const maxZoom = 18; // Maximum zoom level
  const scaledZoom = Math.abs(newZoom - prevZoom) / maxZoom;
  const scaledDistance = distance / maxDistance;
  return Math.min(maxDuration, (scaledDistance + scaledZoom) * maxDuration);
}

export function isInCacheOrWorking(htmlString: string) {
  const key = getHtmlImageCacheKey(htmlString);
  return IMAGE_CACHE.has(key) || cacheWorking.has(key);
}

export const getIconFromCache = (
  html: string,
  anchorX: number | undefined,
  anchorY: number | undefined,
  addToCache: (html: string) => void
) => {
  const cached = getFromCache(html);
  if (cached) {
    return {
      url: cached.dataUrl,
      width: cached.iconSize[0],
      height: cached.iconSize[1],
      // if not defined is half the width
      anchorX: anchorX,
      // anchor is the bottom of the icon where the point of the triangle is
      anchorY: anchorY ?? cached.iconSize[1],
    };
  }
  if (addToCache) {
    addToCache(html);
  }
  return {
    url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    width: 1,
    height: 1,
  };
};

function htmlToElements(html: string): Promise<HTMLDivElement> {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.style.zIndex = '-1';
  div.style.position = 'absolute';
  div.style.top = '0px';
  div.style.left = '0px';
  div.style.display = 'inline-block';
  document.body.appendChild(div);

  const all: (SVGImageElement | HTMLImageElement)[] = [];
  all.push(...div.getElementsByTagName('img'));
  all.push(...div.getElementsByTagName('image'));
  let imagesLoaded = 0;
  if (!all.length) {
    return Promise.resolve(div);
  }
  // wait for all images to load
  return new Promise((res) => {
    all.forEach((image) => {
      image.onload = () => {
        imagesLoaded++;
        if (imagesLoaded === all.length) {
          res(div);
        }
      };
    });
  });
}

export async function getHtmlDataUrl(html: string | HTMLElement) {
  return new Promise<{ dataUrl: string; iconSize: [number, number] }>(async (res) => {
    let htmlString;
    if (html instanceof HTMLElement) {
      htmlString = html.outerHTML;
    } else {
      htmlString = html;
    }

    const key = getHtmlImageCacheKey(htmlString);
    let cached = IMAGE_CACHE.get(key);
    if (!cached && cacheWorking.has(key)) {
      await waitForCache(key);
      cached = IMAGE_CACHE.get(key);
    }
    if (cached) {
      res({ dataUrl: cached.dataUrl, iconSize: cached.iconSize });
      return cached;
    }
    cacheWorking.add(key);
    const htmlElement = await htmlToElements(htmlString);
    const dataUrl = await toPng(htmlElement, {
      pixelRatio: 1,
      skipFonts: true,
    });

    const image = new Image();
    image.src = dataUrl;
    image.onload = () => {
      const iconSize: [number, number] = [image.width, image.height];
      IMAGE_CACHE.set(key, { dataUrl, iconSize });
      cacheWorking.delete(key);
      res({ dataUrl, iconSize });
      document.body.removeChild(htmlElement);
    };
  });
}
